import React from 'react';
import { Box, IconButton, IconButtonProps, InputAdornment, InputBaseComponentProps, LinearProgress, styled, TextField, TextFieldProps as MuiTextFieldProps, Typography } from '@mui/material';

import { useDocumentUploadState } from '../../hooks/useDocumentUploadState';
import { PosthogFeatureFlags } from '../../lib/env/posthogFeatureFlags';
import { StoredFile } from '../../types/sponsorshipApplication';

import { useFeatureFlagEnabled } from 'posthog-js/react';
import prettyBytes from 'pretty-bytes';

export const DEFAULT_ACCEPTED_FILE_EXTENSIONS = '.pdf, .docx, .mp3, .mov, .mp4, .png, .jpeg, .jpg';

const getFileDetails = (value: StoredFile | File | null) => {
  const name = value ? value.name : '';
  const parts = name.split('.');
  const extension = parts.pop() as string;
  const filenameWithoutExtension = parts.join('.');

  return {
    filename: filenameWithoutExtension,
    extension,
  };
};

const Label = styled('label', { shouldForwardProp: (prop) => prop !== 'disabled' })<{ disabled?: boolean }>`
  color: #444444;
  flex-grow: 1;
  margin-left: ${({ disabled }) => (disabled ? '15px' : '0')};
  position: relative;

  input {
    opacity: 0 !important;
  }

  & > span {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 2;
    display: flex;
    align-items: center;
  }

  span.MuiFileInput-placeholder {
    color: gray;
  }
`;

const Filename = styled('div')`
  display: flex;
  width: 100%;

  & > span {
    display: block;
  }

  & > span:first-of-type {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  & > span:last-of-type {
    flex-shrink: 0;
    display: block;
  }
`;

// eslint-disable-next-line react/display-name
export const Input = React.forwardRef(
  (props: InputBaseComponentProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const { id: inputId, text, isPlaceholder, placeholder, disabled, ...restInputProps } = props;
    const isDirectFileUploadEnabled = useFeatureFlagEnabled(PosthogFeatureFlags.DFU);
    const { isUploading, isUploaded, uploadProgress } = useDocumentUploadState(inputId!);

    return (
      <Box my={1}>
        <Label>
          <input {...restInputProps} ref={ref} />
          {
            text
              ? (
                <span
                  aria-placeholder={placeholder}
                  className={isPlaceholder ? 'MuiFileInput-placeholder' : ''}
                >
                  {
                    typeof text === 'string'
                      ? (
                        text
                      )
                      : (
                        <Filename>
                          <span>{text.filename}</span>
                          <span>.{text.extension}</span>
                        </Filename>
                      )
                  }
                </span>
              )
              : null
          }
        </Label>
        {isDirectFileUploadEnabled && !disabled && <LinearProgress style={{ visibility: isUploading || isUploaded ? 'visible' : 'hidden' }} value={uploadProgress} variant='determinate' />}
      </Box>
    );
  },
);

type TextFieldProps = Omit<
  MuiTextFieldProps,
  'onChange' | 'select' | 'type' | 'multiline' | 'defaultValue'
>;

type SingleFile = {
  value?: StoredFile | File | null;
  getInputText?: (file: StoredFile | File | null) => string;
  getSizeText?: (file: StoredFile | File | null) => string;
  onChange?: (value: File | null) => void;
};

type FileInputProps = TextFieldProps & {
  hideSizeText?: boolean;
  clearIconButtonProps?: IconButtonProps;
} & SingleFile;

// eslint-disable-next-line react/display-name
export const FileInput = React.forwardRef(
  (props: FileInputProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      id,
      value,
      onChange,
      disabled,
      className,
      getInputText,
      getSizeText,
      placeholder,
      hideSizeText,
      inputProps,
      InputProps,
      clearIconButtonProps = {},
      helperText,
      ...restTextFieldProps
    } = props;
    const { startAdornment, ...restInputProps } = InputProps || {};
    const { resetUploadProgressState } = useDocumentUploadState(id!);

    const { className: iconButtonClassName = '', ...restClearIconButtonProps }
      = clearIconButtonProps;
    const inputRef = React.useRef<HTMLInputElement>(null);

    const getTotalSizeText = (): string => {
      if (typeof getSizeText === 'function' && value !== undefined && value !== null) {
        return getSizeText(value as StoredFile);
      }

      if (value !== undefined && value !== null && value.size) {
        return prettyBytes(value.size);
      }

      return '';
    };

    const getTheInputText = () => {
      if (value === null) {
        return placeholder || '';
      }

      if (typeof getInputText === 'function' && value !== undefined) {
        return getInputText(value as StoredFile);
      }

      if (value !== undefined) {
        return getFileDetails(value);
      }

      return '';
    };

    const resetInputValue = () => {
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      const files = fileList ? Array.from(fileList) : [];

      onChange?.(files[0] || null);

      if (!files[0]) {
        resetInputValue();
      }
    };

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (disabled) {
        return;
      }

      onChange?.(null);
      resetUploadProgressState();
    };

    return (
      <TextField
        InputProps={
          {
            startAdornment: !disabled
              ? (
                <InputAdornment position='start'>{startAdornment}</InputAdornment>
              )
              : undefined,
            endAdornment: (
              <InputAdornment
                position='end'
                style={{ visibility: value ? 'visible' : 'hidden' }}
              >
                {
                  !hideSizeText
                    ? (
                      <Typography
                        className='MuiFileInput-Typography-size-text'
                        lineHeight={1}
                        mr='2px'
                        variant='caption'
                      >
                        {getTotalSizeText()}
                      </Typography>
                    )
                    : null
                }
                {
                  disabled
                    ? null
                    : (
                      <IconButton
                        aria-label='Clear'
                        className={`${iconButtonClassName} MuiFileInput-ClearIconButton`}
                        disabled={disabled}
                        onClick={handleClear}
                        size='small'
                        title='Clear'
                        {...restClearIconButtonProps}
                      />
                    )
                }
              </InputAdornment>
            ),
            ...restInputProps,
            inputProps: {
              id,
              text: getTheInputText(),
              isPlaceholder: !value,
              placeholder,
              accept: DEFAULT_ACCEPTED_FILE_EXTENSIONS,
              ...inputProps,
              ...InputProps?.inputProps,
            },
            inputComponent: Input,
            sx: {
              '& > .MuiOutlinedInput-notchedOutline': {
                height: '100%',
              },
              paddingY: 0,
              paddingX: 1,
            },
          }
        }
        className={`MuiFileInput-TextField ${className || ''}`}
        disabled={disabled}
        helperText={!disabled ? helperText : ''}
        onChange={handleChange}
        ref={ref}
        sx={
          {
            '& .Mui-disabled': {
              backgroundColor: '#F5F5F5',
              cursor: 'pointer !important',
            },
            '& .Mui-disabled > *': {
              cursor: 'pointer !important',
            },
          }
        }
        type='file'
        {...restTextFieldProps}
      />
    );
  },
);

export default FileInput;
